import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../../components/LanguageSwitcher';
import { useState } from 'react';
import GlowButton from '../../../components/GlowButton';
import VideoModal from '../../../components/VideoModal';

export default function Hero() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  
  const handleScroll = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <section 
      className="relative min-h-screen flex flex-col overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #000000 0%, #0a0e14 15%, #1b2433 40%, #1b2433 60%, #0a0e14 85%, #000000 100%)'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/5 via-transparent to-green-900/5"></div>
      
      {/* Navigation Bar */}
      <nav className="relative z-20 w-full px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/assets/media/automat-logo.png"
              alt="Automat Logo" 
              className="h-8 sm:h-10 w-auto object-contain"
            />
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-6">
            <a 
              href="#solution" 
              onClick={(e) => handleScroll(e, 'solution')}
              className="text-base text-gray-300 hover:text-purple-400 transition-colors duration-300 cursor-pointer whitespace-nowrap"
            >
              {t('nav.features')}
            </a>
            <LanguageSwitcher />
            <a 
              href="https://painel.goautomat.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-base font-semibold transition-colors duration-300 cursor-pointer whitespace-nowrap"
              style={{ color: '#01efac' }}
            >
              {t('nav.login')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-gray-900/95 backdrop-blur-lg border-t border-gray-800 shadow-xl">
            <div className="px-4 py-6 space-y-4">
              <a 
                href="#solution" 
                onClick={(e) => handleScroll(e, 'solution')}
                className="block text-base text-gray-300 hover:text-purple-400 transition-colors duration-300 cursor-pointer py-2"
              >
                {t('nav.features')}
              </a>
              <div className="py-2">
                <LanguageSwitcher />
              </div>
              <a 
                href="https://painel.goautomat.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block text-base font-semibold transition-colors duration-300 cursor-pointer py-2"
                style={{ color: '#01efac' }}
              >
                {t('nav.login')}
              </a>
            </div>
          </div>
        )}
      </nav>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-20 text-center flex-1 flex flex-col items-center justify-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full mb-8">
          <i className="ri-sparkling-2-fill text-purple-400"></i>
          <span className="text-purple-300 text-sm font-medium">{t('hero.badge')}</span>
        </div>

        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
          {t('hero.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-green-400">{t('hero.titleHighlight')}</span><br />
          {t('hero.titleEnd')}
        </h1>

        <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
          {t('hero.subtitle')}<br />
          {t('hero.subtitleEnd')}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
          <GlowButton variant="primary" onClick={() => setVideoModalOpen(true)}>
            <i className="ri-play-circle-fill text-xl"></i>
            {t('hero.watchDemo')}
          </GlowButton>

          <GlowButton 
            variant="outline"
            href="https://wa.me/5519988332466?text=Ol%C3%A1%2C%20estou%20vindo%20pelo%20site%20da%20Automat."
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="ri-whatsapp-fill text-xl"></i>
            {t('hero.talkToConsultant')}
          </GlowButton>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-green-400 mb-2">24/7</div>
            <div className="text-gray-400 text-sm">{t('hero.stats.support')}</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-green-400 mb-2">90%</div>
            <div className="text-gray-400 text-sm">{t('hero.stats.reduction')}</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-green-400 mb-2">3x</div>
            <div className="text-gray-400 text-sm">{t('hero.stats.conversions')}</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-green-400 mb-2">Zero</div>
            <div className="text-gray-400 text-sm">{t('hero.stats.leads')}</div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-950 to-transparent"></div>

      <VideoModal 
        isOpen={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        videoUrl="https://youtu.be/wjB-jFEXMsw?si=7xRkqCtSrcGdPfy4"
      />
    </section>
  );
}
